import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChildren,
} from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';

import type { OtpVerifyRequest } from '@core/auth/auth.models';
import { APP_CONFIG } from '@core/config/app-config';
import { FormField } from '@shared/components/form-field/form-field';
import { Spinner } from '@shared/components/spinner/spinner';
import { TextInput } from '@shared/components/text-input/text-input';
import {
  isValidMobile,
  normaliseMobile,
  mobileValidator,
} from '@shared/validators/mobile.validator';
import { OTP_LENGTH, digitsOnly, isValidOtp, otpValidator } from '@shared/validators/otp.validator';
import { requiredTrim } from '@shared/validators/required-trim.validator';

/**
 * What the server said last time RequestOtp was called.
 *
 * `token` increments on every reply, including two replies with identical
 * second counts - which is the point. Without it, a second resend that also
 * returns 30 seconds would not change any input value, the restart effect
 * would not re-run, and the countdown would sit frozen at whatever it had
 * counted down to.
 *
 * `canEnterCode` is false when the SEND itself was throttled (HTTP 429): there
 * is no new code to type, only a wait.
 */
export interface OtpChallengeState {
  readonly token: number;
  readonly resendAfterSeconds: number;
  readonly expiresInSeconds: number;
  readonly canEnterCode: boolean;
}

/**
 * Phone + one-time code, the second of the two mandated methods.
 *
 * The non-enumeration rule governs everything visible here: RequestOtp returns
 * the SAME body for a registered and an unregistered number, so this component
 * renders the same notice either way and has no branch that could betray the
 * difference. `notice` is supplied by the parent and is deliberately
 * conditional in its wording.
 */
@Component({
  selector: 'app-otp-login-form',
  imports: [ReactiveFormsModule, FormField, TextInput, Spinner],
  templateUrl: './otp-login-form.html',
  styleUrl: './otp-login-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtpLoginForm {
  private readonly fb = inject(NonNullableFormBuilder);

  // -- Inputs --------------------------------------------------------------

  /** RequestOtp is in flight. */
  readonly sending = input(false);
  /** VerifyOtp is in flight. */
  readonly verifying = input(false);
  /** Inline failure - wrong code, expired, attempts exceeded, throttled. */
  readonly errorMessage = input('');
  /** Non-committal confirmation. Never says whether the number is registered. */
  readonly notice = input('');
  readonly challenge = input<OtpChallengeState | null>(null);

  readonly requestOtp = output<string>();
  readonly submitted = output<OtpVerifyRequest>();

  // -- Form ----------------------------------------------------------------

  protected readonly form = this.fb.group({
    mobileNumber: this.fb.control('', [requiredTrim(), mobileValidator()]),
    otp: this.fb.control('', [otpValidator(OTP_LENGTH)]),
    rememberMe: this.fb.control(false),
  });

  /**
   * The six boxes' display state.
   *
   * The FormControl remains the form of record - it carries the validator and
   * the submitted value - but the per-box view needs a signal, because the app
   * is zoneless and a paste handler writing a plain array would repaint
   * nothing.
   */
  protected readonly digits = signal<readonly string[]>(emptyDigits());

  private readonly boxes = viewChildren<ElementRef<HTMLInputElement>>('otpBox');

  private readonly attempted = signal(false);

  // -- Countdowns ----------------------------------------------------------

  private readonly resendIn = signal(0);
  private readonly expiresIn = signal(0);
  private ticker: ReturnType<typeof setInterval> | null = null;

  /** Seconds until a resend is allowed. Public so specs can assert the tick. */
  readonly resendCountdown = this.resendIn.asReadonly();

  /** Seconds until the current code stops working. */
  readonly expiryCountdown = this.expiresIn.asReadonly();

  protected readonly codeVisible = computed(() => this.challenge()?.canEnterCode === true);

  protected readonly canResend = computed(() => this.resendIn() === 0 && !this.sending());

  /**
   * The code has aged out. Distinct from "wrong code": the remedy is a resend,
   * not a retype, so saying so saves the user five wasted attempts.
   */
  protected readonly expired = computed(() => {
    const challenge = this.challenge();
    return (
      challenge !== null &&
      challenge.canEnterCode &&
      challenge.expiresInSeconds > 0 &&
      this.expiresIn() === 0
    );
  });

  protected readonly resendLabel = computed(() => {
    if (this.sending()) return 'Sending';
    const seconds = this.resendIn();
    if (seconds > 0) return `Resend in ${seconds}s`;
    return this.challenge() === null ? 'Send OTP' : 'Resend code';
  });

  constructor() {
    effect(() => {
      // Restarts both countdowns whenever the server answers. Deliberately
      // does NOT read resendIn/expiresIn - the interval writes them, and
      // reading them here would make this effect re-run once per second.
      const challenge = this.challenge();
      this.stopTicking();

      const resend = challenge ? clampSeconds(challenge.resendAfterSeconds) : 0;
      const expires = challenge ? clampSeconds(challenge.expiresInSeconds) : 0;
      this.resendIn.set(resend);
      this.expiresIn.set(expires);

      if (resend > 0 || expires > 0) this.startTicking();
    });

    inject(DestroyRef).onDestroy(() => this.stopTicking());
  }

  // -- Field errors --------------------------------------------------------

  protected mobileError(): string {
    const control = this.form.controls.mobileNumber;
    if (!this.attempted() && !control.touched) return '';
    if (control.hasError('required')) return 'Enter your registered mobile number.';
    if (control.hasError('mobile')) return 'Enter a 10-digit mobile number.';
    return '';
  }

  protected otpError(): string {
    if (this.expired()) return 'That code has expired. Request a new one.';
    if (!this.attempted()) return '';
    const value = this.form.controls.otp.value;
    if (!value) return `Enter the ${OTP_LENGTH}-digit code.`;
    if (!isValidOtp(value, OTP_LENGTH)) return `The code is ${OTP_LENGTH} digits.`;
    return '';
  }

  // -- Actions -------------------------------------------------------------

  /** Enter sends the code at the phone stage and verifies at the code stage. */
  protected onSubmit(): void {
    if (!this.codeVisible()) {
      this.sendOtp();
      return;
    }
    this.verify();
  }

  protected sendOtp(): void {
    const control = this.form.controls.mobileNumber;
    control.markAsTouched();
    this.attempted.set(true);

    const raw = control.value.trim();
    if (!isValidMobile(raw)) return;

    // Any previously typed code is dead: a resend invalidates the previous
    // code server-side, so leaving the old digits on screen invites the user
    // to submit a code that cannot work.
    this.clearDigits();
    this.requestOtp.emit(normaliseMobile(raw));
  }

  protected verify(): void {
    this.attempted.set(true);

    // getRawValue() is safe here, unlike on the Person form: this group has no
    // conditionally disable()d control, so nothing can be resurrected. It is
    // also necessary - NonNullableFormBuilder types `form.value` as Partial<T>,
    // while getRawValue() returns the complete non-optional shape.
    const value = this.form.getRawValue();
    if (!isValidMobile(value.mobileNumber) || !isValidOtp(value.otp, OTP_LENGTH)) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.expired()) return;

    this.submitted.emit({
      mobileNumber: normaliseMobile(value.mobileNumber),
      otp: value.otp,
      rememberMe: value.rememberMe,
    });
  }

  /** Called by the parent after a rejected code, so the user types into empty boxes. */
  clearDigits(): void {
    this.writeDigits(emptyDigits());
  }

  // -- Six-box entry -------------------------------------------------------

  /**
   * One box changed.
   *
   * Handles the multi-character case too: a phone's SMS autofill and some
   * password managers drop the whole code into whichever box has focus, and a
   * `maxlength` alone would silently truncate it to one digit.
   */
  protected onDigitInput(event: Event, index: number): void {
    const element = event.target as HTMLInputElement;
    const typed = digitsOnly(element.value, OTP_LENGTH);

    if (typed.length > 1) {
      this.distribute(typed, index);
      return;
    }

    const next = [...this.digits()];
    next[index] = typed;
    this.writeDigits(next);

    if (typed) this.focusBox(index + 1);
  }

  /**
   * Keyboard navigation.
   *
   * Backspace on an empty box steps back AND clears - the behaviour people
   * expect from a code field, where the alternative is pressing backspace
   * twice per digit.
   */
  protected onDigitKeydown(event: KeyboardEvent, index: number): void {
    const current = this.digits();

    if (event.key === 'Backspace') {
      event.preventDefault();
      const next = [...current];
      if (next[index]) {
        next[index] = '';
        this.writeDigits(next);
      } else if (index > 0) {
        next[index - 1] = '';
        this.writeDigits(next);
        this.focusBox(index - 1);
      }
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      const next = [...current];
      next[index] = '';
      this.writeDigits(next);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusBox(index - 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusBox(index + 1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      this.focusBox(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      this.focusBox(OTP_LENGTH - 1);
    }
  }

  /** A pasted code spreads across the boxes from the one that received it. */
  protected onDigitPaste(event: ClipboardEvent, index: number): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    const code = digitsOnly(pasted, OTP_LENGTH);
    if (!code) return;

    event.preventDefault();
    this.distribute(code, index);
  }

  protected boxLabel(index: number): string {
    return `Digit ${index + 1} of ${OTP_LENGTH}`;
  }

  // -- Internals -----------------------------------------------------------

  private distribute(code: string, from: number): void {
    const next = [...this.digits()];
    // A code pasted into the middle of the group still starts at box 0 when it
    // is the full length - that is what the user meant.
    const start = code.length >= OTP_LENGTH ? 0 : from;

    for (let i = 0; i < code.length && start + i < OTP_LENGTH; i++) {
      next[start + i] = code.charAt(i);
    }

    this.writeDigits(next);
    this.focusBox(Math.min(start + code.length, OTP_LENGTH - 1));
  }

  /**
   * The single mutation point: signal, FormControl and DOM, in that order.
   *
   * The DOM write is not redundant. When a box already holds '5' and the user
   * types '7' into it, the element reads '57' while state says '5'; the
   * `[value]` binding sees no change and leaves the stale '57' on screen.
   * Reconciling explicitly is what keeps what is displayed equal to what will
   * be submitted.
   */
  private writeDigits(next: readonly string[]): void {
    const normalised = next.map((digit) => digitsOnly(digit, 1));
    this.digits.set(normalised);
    this.form.controls.otp.setValue(normalised.join(''));
    for (const [index, box] of this.boxes().entries()) {
      box.nativeElement.value = normalised[index] ?? '';
    }
  }

  private focusBox(index: number): void {
    const boxes = this.boxes();
    if (boxes.length === 0) return;
    const target = boxes[Math.min(Math.max(index, 0), boxes.length - 1)];
    target?.nativeElement.focus();
    target?.nativeElement.select();
  }

  private startTicking(): void {
    this.ticker = setInterval(() => {
      this.resendIn.update((n) => (n > 0 ? n - 1 : 0));
      this.expiresIn.update((n) => (n > 0 ? n - 1 : 0));
      // Stop rather than tick forever against two zeroes - a login screen left
      // open on a control-room display would otherwise wake once a second all
      // shift.
      if (this.resendIn() === 0 && this.expiresIn() === 0) this.stopTicking();
    }, 1000);
  }

  private stopTicking(): void {
    if (this.ticker !== null) {
      clearInterval(this.ticker);
      this.ticker = null;
    }
  }
}

function emptyDigits(): readonly string[] {
  return Array.from({ length: OTP_LENGTH }, () => '');
}

/** Guards against a negative or absurd server value driving the countdown. */
function clampSeconds(seconds: number): number {
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  return Math.min(Math.ceil(seconds), APP_CONFIG.otp.ttlSeconds * 4);
}
